import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Inject,
  Input,
  OnInit
}                         from '@angular/core';
import {
  BlockUI,
  NgBlockUI
}                         from 'ng-block-ui';

import {
  HaynesProVesaGroupModel,
  HaynesProVesaSelectionModel,
  HaynesProVesaSubGroupModel,
  HaynesProVesaSystemModel
}                              from './typings/haynes-pro.vesa';
import { HaynesProService }    from '../../haynes-pro.service';
import { MessagesService }     from '../../../messages/messages.service';
import { VehicleModel }        from '../../../vehicles/models/vehicle';
import { VesaService }         from './vesa.service';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-vesa',
  styles: [ require('./haynes-pro.diagnostic-tool.vesa.component.scss') ],
  template: require('./haynes-pro.diagnostic-tool.vesa.component.html')
})
export class HaynesProDiagnosticToolVesaComponent implements OnInit {

  @BlockUI('haynesProVesaBlock') block : NgBlockUI;
  @Input() isActive : boolean;
  @Input() vehicle  : VehicleModel;

  group     : HaynesProVesaGroupModel;
  groups    : Array<HaynesProVesaGroupModel>;
  subGroups : Array<HaynesProVesaSubGroupModel>;
  systems   : Array<HaynesProVesaSystemModel>;

  hasSubGroups = false;
  selections   : { [groupId: string]: HaynesProVesaSelectionModel } = {};

  constructor (
    private cdr              : ChangeDetectorRef,
    @Inject(forwardRef(() => HaynesProService))
    private haynesProService : HaynesProService,
    private messagesService  : MessagesService,
    private vesaService      : VesaService
  ) {}

  ngOnInit () : void {
    this.block.start();

    this.vesaService.getVesaSystems(this.vehicle)
    .then(( groups : Array<HaynesProVesaGroupModel> ) => {
      this.groups = groups;

      if ( !this.isActive ) {
        this.haynesProService.updateSubscriptionStatus(true);
      }
    })
    .catch(( err : any ) => {
      this.messagesService.error(err);
    })
    .finally(() => {
      this.block.stop();

      if (this.isActive && this.groups?.length) {
        this.groups[0].selected = true;
      }
    });
  }

  get selectionsList () : Array<HaynesProVesaSelectionModel> {
    return Object.values(this.selections);
  }

  get hasSelections () : boolean {
    return this.selectionsList.length > 0;
  }

  handleGroupSelection ( group : HaynesProVesaGroupModel ) : void {
    this.group        = group;
    this.systems      = null;
    this.hasSubGroups = group.sub_groups?.length > 0;

    if (this.subGroups) {
      this.subGroups.forEach(( sg : any ) => sg.selected = false);
    }

    if (this.hasSubGroups) {
      this.subGroups = group.sub_groups;
      this.systems   = null;

      this.restoreSubGroupState(group);
    } else {
      this.subGroups = null;
      this.systems   = group.systems;
      this.restoreSystemIcons(group.systems, group.group_id);
    }

    this.cdr.detectChanges();
  }

  handleSubGroupSelection ( subGroup : HaynesProVesaSubGroupModel ) : void {
    this.systems = subGroup.systems;
    this.restoreSystemIcons(subGroup.systems, subGroup.group_id);

    this.cdr.detectChanges();
  }

  handleSystemSelection ( system : HaynesProVesaSystemModel ) : void {
    const groupId   = this.getActiveGroupId();
    const groupText = this.getActiveGroupText();

    // Clear previous selection icon for this group
    const previous  = this.selections[groupId];
    const previousId = previous ? this.getSystemId(previous.system) : null;
    const currentId  = this.getSystemId(system);

    if (previous) {
      previous.system.listIcon = '';
    }

    // If clicking the same system, deselect it
    if (previous && previousId && previousId === currentId) {
      delete this.selections[groupId];
      system.listIcon = '';
    } else {
      system.listIcon = 'check';
      this.selections[groupId] = {
        group_id  : groupId,
        group_text: groupText,
        source    : this.group?.source || 'vesa',
        system    : system
      };
    }

    this.cdr.detectChanges();
  }

  proceed () : void {
    const data = {
      selections: this.selectionsList.map(s => ({
        description              : s.system.description,
        group_id                 : s.group_id,
        group_text               : s.group_text,
        source                   : s.source,
        system_type_id           : s.system.system_type_id,
        wiring_diagram_system_id : s.system.wiring_diagram_system_id
      })),
      vehicle_id: this.vehicle.id
    };

    this.haynesProService.flipDiagnosticTool('back', 'vesa-details', data);
  }

  removeSelection ( groupId : string ) : void {
    const selection = this.selections[groupId];

    if (selection) {
      selection.system.listIcon = '';
      delete this.selections[groupId];
    }

    this.cdr.detectChanges();
  }

  private getActiveGroupId () : string {
    if (this.hasSubGroups && this.subGroups) {
      const selected = this.subGroups.find(sg => sg.selected);

      return selected ? selected.group_id : this.group.group_id;
    }

    return this.group.group_id;
  }

  private getSystemId ( system : HaynesProVesaSystemModel ) : string | null {
    return system?.system_type_id || system?.wiring_diagram_system_id || null;
  }

  private getActiveGroupText () : string {
    if (this.hasSubGroups && this.subGroups) {
      const selected = this.subGroups.find(sg => sg.selected);

      return selected ? selected.group_text : this.group.group_text;
    }

    return this.group.group_text;
  }

  private restoreSubGroupState ( group : HaynesProVesaGroupModel ) : void {
    for (const subGroup of group.sub_groups) {
      if (this.selections[subGroup.group_id]) {
        subGroup.selected = true;
        this.systems = subGroup.systems;
        this.restoreSystemIcons(subGroup.systems, subGroup.group_id);

        return;
      }
    }
  }

  private restoreSystemIcons ( systems : Array<HaynesProVesaSystemModel>, groupId : string ) : void {
    if (!systems) {
      return;
    }

    const selection   = this.selections[groupId];
    const selectionId = selection ? this.getSystemId(selection.system) : null;

    systems.forEach(( system : HaynesProVesaSystemModel ) => {
      system.selected = false;
      system.listIcon = (selectionId && selectionId === this.getSystemId(system))
        ? 'check'
        : '';
    });
  }
}
